import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Field, Form as FormikForm, useFormikContext } from 'formik';
import * as PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@material-ui/core';

import { useCountdown } from '@edf-pkg/app-main';

import SubmitWithLoading from '$app-web/components/submit-with-loading';
import { ON_PREMISE_INSTANCES, VERIFICATION_CODE_LENGTH } from '$app-web/constants';
import TextLink from '$app-web/modules/auth/components/text-link';
import CustomInputTextField from '$app-web/modules/auth/pages/sign-up/components/custom-input-text-field';
import LoadingButton from '$app-web/modules/rdash/components/loading-button';
import { getEnvironmentName, getIsACT, URLsManager } from '$app-web/utils';

import useStyles from './styles';

import { FontAwesomeIcon } from '$common/fontawesome-pro/react-fontawesome';

const Form = (props) => {
    const { isMFAEnabled, signIn } = props;
    const classes = useStyles();
    const { t } = useTranslation();
    const [timeLeft, startCounter, resetCounter] = useCountdown();

    const { values, setFieldValue, isSubmitting, setSubmitting } = useFormikContext();

    const isACT = useMemo(() => getIsACT(), []);

    const [timerStarted, setTimerStarted] = useState(false);

    const isEDENInstance = useMemo(() => {
        return getEnvironmentName() === ON_PREMISE_INSTANCES.EDEN;
    }, []);

    const handleOTPSignIn = useCallback(
        (inputValues = {}) => {
            signIn(inputValues, { setSubmitting });
        },
        [setSubmitting, signIn]
    );

    useEffect(() => {
        if (!timerStarted && isMFAEnabled) {
            setTimerStarted(true);
            startCounter(60);
        }
    }, [isMFAEnabled, startCounter, timerStarted]);

    useEffect(() => {
        if (values.token.length === VERIFICATION_CODE_LENGTH) {
            handleOTPSignIn({ token: values.token });
        }
    }, [handleOTPSignIn, values.token]);

    const sendNewCode = useCallback(() => {
        setFieldValue('token', '');
        handleOTPSignIn();
        setTimerStarted(false);
        resetCounter();
    }, [handleOTPSignIn, resetCounter, setFieldValue]);

    return (
        <>
            <FormikForm>
                <Field
                    name="username"
                    placeholder={isEDENInstance ? t('pdash:identifier') : t('pdash:email_address')}
                    type="email"
                    component={CustomInputTextField}
                    fullWidth
                    margin="normal"
                    startAdornment={<FontAwesomeIcon icon={['fal', 'envelope']} />}
                    showHelperTextOnError={false}
                    className={clsx({ [classes.hiddenField]: isMFAEnabled })}
                />
                <Field
                    name="password"
                    placeholder={t('pdash:password')}
                    type="password"
                    component={CustomInputTextField}
                    fullWidth
                    margin="normal"
                    startAdornment={<FontAwesomeIcon icon={['fal', 'lock-alt']} />}
                    showHelperTextOnError={false}
                    className={clsx({ [classes.hiddenField]: isMFAEnabled })}
                />
                {isMFAEnabled && (
                    <Typography variant="body2" gutterBottom align="center">
                        {t('pdash:sign_in_mfa_code_description')}
                    </Typography>
                )}
                <Field
                    name="token"
                    placeholder={t('pdash:code')}
                    type={!isMFAEnabled ? 'hidden' : 'text'}
                    component={CustomInputTextField}
                    fullWidth
                    margin="normal"
                    showHelperTextOnError={false}
                    className={!isMFAEnabled ? classes.hiddenField : ''}
                    inputProps={{ style: { textAlign: 'center' } }}
                    disabled={isSubmitting}
                />
                {!isMFAEnabled && (
                    <>
                        <Typography variant="subtitle2" gutterBottom className={classes.forgotPasswordWrapper} align="center">
                            <TextLink
                                to={URLsManager.to('dashboard:auth:FORGOT_PASSWORD')}
                                color={isACT ? 'actHealth' : 'default'}
                            >
                                {t('pdash:login_forgot_password')}
                            </TextLink>
                        </Typography>
                        <div className={classes.submitButtonWrapper}>
                            <SubmitWithLoading
                                classes={{ submitButton: isACT ? 'containedACTColor' : undefined }}
                                text={t('pdash:login_button_text')}
                                isSubmitting={isSubmitting}
                                buttonProps={{
                                    variant: 'contained',
                                    color: 'secondary',
                                    fullWidth: true,
                                }}
                            />
                        </div>
                    </>
                )}
                <br />
                {isMFAEnabled && (
                    <Grid item container justify="center">
                        {timeLeft > 0 && (
                            <Typography variant="body2">
                                {timeLeft === 1 && t('pdash:validation_send_new_code_counter_single', [1])}
                                {timeLeft !== 1 && t('pdash:validation_send_new_code_counter_plural', [timeLeft])}
                            </Typography>
                        )}
                        {timeLeft === 0 && <LoadingButton title={t('pdash:validation_send_new_code')} onClick={sendNewCode} />}
                    </Grid>
                )}
                {!isMFAEnabled && !isEDENInstance && (
                    <Grid item className={classes.bottomTextWrapper}>
                        <Typography variant="subtitle2" color="textPrimary" align="center" display="block" gutterBottom>
                            {`${t('pdash:login_dont_have_account')} `}
                            <TextLink to={URLsManager.to('dashboard:auth:SIGN_UP')} color={isACT ? 'actHealth' : 'default'}>
                                {t('pdash:login_signup_text')}
                            </TextLink>
                        </Typography>
                    </Grid>
                )}

                {isMFAEnabled && (
                    <Grid item className={classes.bottomTextWrapper}>
                        <Typography variant="subtitle2" color="textPrimary" align="center" display="block" gutterBottom>
                            {`${t('pdash:sign_in_no_access_to_phone')} `}
                            <TextLink to="mailto:support@avicennaresearch.com" external>
                                {t('pdash:contact_us')}
                            </TextLink>
                        </Typography>
                    </Grid>
                )}
            </FormikForm>
        </>
    );
};

Form.propTypes = {
    isMFAEnabled: PropTypes.bool.isRequired,
};
export default Form;
